import cx from "classnames";
import React, { ReactNode, useCallback } from "react";
import slugify from "slugify";

import { Text } from "@/components/elements/Text";
import useId from "@/hooks/useId";

import styles from "./styles.module.css";

type ButtonType = "increase" | "decrease";

const BUTTON = ({
  stepperType,
  updateValue,
  disabled = false,
}: {
  stepperType: "increase" | "decrease";
  updateValue: (type: ButtonType) => void;
  disabled?: boolean;
}) => {
  const buttonSymbol = stepperType === "increase" ? "+" : "‒";
  const label =
    stepperType === "increase" ? "Increase amount" : "Decrease amount";
  return (
    <button
      aria-label={label}
      onClick={() => {
        updateValue(stepperType);
      }}
      disabled={disabled}
      className={styles.button}
      type="button"
    >
      {buttonSymbol}
    </button>
  );
};

export type NumberStepperProps = {
  name: string;
  value: number;
  label: string | ReactNode;
  hideLabel?: boolean;
  onChange: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  className?: string;
};

export const NumberStepper = ({
  name,
  value,
  onChange,
  label,
  hideLabel = false,
  minValue = 0,
  maxValue = Infinity,
  className,
}: NumberStepperProps) => {
  const id = useId(
    `${slugify(
      name || `random-${Math.random().toString(36).substr(2, 9)}`
    )}-number-stepper`
  );
  const updateValue = useCallback(
    (buttonType: ButtonType) => {
      const newValue = buttonType === "increase" ? value + 1 : value - 1;
      // When value increasing, check it's not above max
      if (newValue > value && newValue > maxValue) return;
      // When value decreasing, check it's not below min
      // e.g. allow 2 => 1 when stock is -17.
      if (newValue < value && newValue < minValue) return;
      onChange(newValue);
    },
    [value, onChange]
  );
  return (
    <div className={cx(styles.container, className)}>
      <Text
        as="label"
        className={cx(styles.label, { visuallyhidden: hideLabel })}
        htmlFor={id}
      >
        {label}
      </Text>
      <div className={styles.buttonGroup}>
        <BUTTON
          stepperType="decrease"
          updateValue={updateValue}
          disabled={value <= minValue}
        />
        <input
          value={value}
          id={id}
          type="number"
          className={styles.inputValue}
          readOnly
        />
        <BUTTON
          stepperType="increase"
          updateValue={updateValue}
          disabled={value >= maxValue}
        />
      </div>
    </div>
  );
};
